import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { useAppTranslation } from "./components/TranslationContext";
import s from './Navigation.module.css';

export const Navigation = () => {
    const { user, logout, isAuthenticated, language, setLanguage, userRole } = useAuth();
    const [menuOpen, setMenuOpen]             = useState(false);
    const [teacherMenuOpen, setTeacherMenuOpen] = useState(false);
    const [mobileOpen, setMobileOpen]         = useState(false);
    const [avatarUrl, setAvatarUrl]           = useState("/images/user.png");
    const navigate      = useNavigate();
    const menuRef       = useRef();
    const teacherMenuRef = useRef();
    const location      = useLocation();
    const isHome        = location.pathname === "/" || location.pathname === "/about";

    const { i18n }           = useTranslation();
    const { t, setLang, lang } = useAppTranslation();

    // Close mobile panel on route change
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    // Lock body scroll when mobile panel is open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    useEffect(() => {
        if (language) { i18n.changeLanguage(language); setLang(language); }
    }, [language, i18n, setLang]);

    const changeLanguage = (newLang) => {
        i18n.changeLanguage(newLang);
        setLanguage(newLang);
        setLang(newLang);
        localStorage.setItem('language', newLang);
    };

    useEffect(() => {
        if (!user?.id) return;
        fetch(`https://api.aiassessment.uz/api/custom-users/?user=${user.id}`)
            .then(r => r.json())
            .then(d => { if (d?.[0]?.img) setAvatarUrl(d[0].img); })
            .catch(() => {});
    }, [user]);

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
            if (teacherMenuRef.current && !teacherMenuRef.current.contains(e.target)) setTeacherMenuOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleLogout = () => {
        logout();
        setMenuOpen(false);
        setMobileOpen(false);
        navigate('/');
    };

    const isTeacherActive = location.pathname.startsWith('/teacher');
    const teacherLinks = [
        { to: '/teacher/themes',    label: t('teacherThemes')    || 'Mavzular' },
        { to: '/teacher/questions', label: t('teacherQuestions') || 'Savollar' },
        { to: '/teacher/groups',    label: t('teacherGroups')    || 'Guruhlar' },
        { to: '/teacher/tests',     label: t('teacherTests')     || 'Testlar' },
        { to: '/teacher/students',  label: t('teacherStudents')  || 'Talabalar' },
    ];

    const navClass = `${s.nav} ${isHome ? s.navHome : ''}`;
    const linkClass = ({ isActive }) => `${s.link} ${isActive ? s.linkActive : ''}`;
    const mobileLinkClass = ({ isActive }) => `${s.mobileLink} ${isActive ? s.mobileLinkActive : ''}`;
    const mobileSubLinkClass = ({ isActive }) => `${s.mobileSubLink} ${isActive ? s.mobileSubLinkActive : ''}`;

    return (
        <>
        <nav className={navClass}>

            {/* ── Logo ── */}
            <div className={s.navLeft}>
                <img className={s.logoImg} src="/images/logo.png" alt="logo" />
                <Link className={s.logoText} to="/">
                    <span className={s.logoTitle}>{t('logoTitle')}</span>
                </Link>
            </div>

            {/* ── Links (desktop) ── */}
            <div className={s.links}>

                {userRole === 1 && (
                    <div ref={teacherMenuRef} className={s.teacherWrap}>
                        <button
                            className={`${s.teacherBtn} ${isTeacherActive ? s.linkActive : ''}`}
                            onClick={() => setTeacherMenuOpen(p => !p)}
                        >
                            {t('teacherPanel') || 'Pedagog paneli'}
                            <svg
                                className={`${s.teacherChevron} ${teacherMenuOpen ? s.teacherChevronOpen : ''}`}
                                width="12" height="12" viewBox="0 0 24 24" fill="currentColor"
                            >
                                <path d="M7 10l5 5 5-5z"/>
                            </svg>
                        </button>
                        {teacherMenuOpen && (
                            <div className={s.teacherDropdown}>
                                {teacherLinks.map(({ to, label }) => (
                                    <NavLink
                                        key={to}
                                        to={to}
                                        className={({ isActive }) =>
                                            `${s.teacherDropdownLink} ${isActive ? s.teacherDropdownLinkActive : ''}`
                                        }
                                        onClick={() => setTeacherMenuOpen(false)}
                                    >
                                        {label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {userRole === 2 && (
                    <NavLink to="/admin/roles" className={linkClass}>
                        {t('adminPanel') || 'Admin paneli'}
                    </NavLink>
                )}

                <NavLink to="/about" className={linkClass}>
                    {t('menuAbout')}
                </NavLink>
            </div>

            {/* ── Right: lang + avatar + burger ── */}
            <div className={s.navInfo}>

                {/* Language switcher (desktop) */}
                <div className={s.langGroup}>
                    {['ru', 'en', 'uz'].map(l => (
                        <button
                            key={l}
                            className={`${s.langBtn} ${lang === l ? s.langActive : ''}`}
                            onClick={() => changeLanguage(l)}
                        >
                            {l.charAt(0).toUpperCase() + l.slice(1)}
                        </button>
                    ))}
                </div>

                {/* Avatar / Login (desktop) */}
                <div className={s.loginBlock}>
                    {isAuthenticated ? (
                        <>
                            <img
                                src={avatarUrl}
                                alt="User"
                                className={s.avatar}
                                onClick={() => setMenuOpen(p => !p)}
                            />
                            {menuOpen && (
                                <div className={s.profileMenu} ref={menuRef}>
                                    {user && (
                                        <div className={s.profileInfo}>
                                            <p className={s.profileName}>{user.username}</p>
                                            <p className={s.profileEmail}>{user.email}</p>
                                        </div>
                                    )}
                                    <NavLink
                                        to="/profile"
                                        className={s.profileMenuItem}
                                        onClick={() => setMenuOpen(false)}
                                    >
                                        <img src="https://cdn-icons-png.flaticon.com/128/9308/9308015.png" alt="" />
                                        {t('profile')}
                                    </NavLink>
                                    <button className={s.profileMenuItem} onClick={handleLogout}>
                                        <img src="https://cdn-icons-png.flaticon.com/128/1286/1286853.png" alt="" />
                                        {t('logout')}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <Link to="/login" className={s.loginLink}>
                            <span className={s.loginLinkText}>{t('login')}</span>
                        </Link>
                    )}
                </div>

                {/* Burger (mobile only) */}
                <button
                    className={`${s.burger} ${mobileOpen ? s.burgerOpen : ''}`}
                    onClick={() => setMobileOpen(p => !p)}
                    aria-label="Menu"
                >
                    <span className={s.burgerLine} />
                    <span className={s.burgerLine} />
                    <span className={s.burgerLine} />
                </button>
            </div>
        </nav>

        {/* ── Mobile panel ── */}
        {mobileOpen && (
            <>
                {/* Overlay */}
                <div className={s.mobileOverlay} onClick={() => setMobileOpen(false)} />

                {/* Drawer */}
                <div className={s.mobilePanel}>
                    <div className={s.mobilePanelHeader}>
                        <span className={s.mobilePanelTitle}>{t('logoTitle') || 'Menyu'}</span>
                        <button className={s.mobilePanelClose} onClick={() => setMobileOpen(false)}>×</button>
                    </div>

                    <div className={s.mobileLinks}>

                        {/* User info */}
                        {isAuthenticated && user && (
                            <div className={s.mobileUserInfo}>
                                <img src={avatarUrl} alt="avatar" className={s.mobileAvatar} />
                                <div>
                                    <p className={s.mobileUserName}>{user.first_name || user.username}</p>
                                    <p className={s.mobileUserEmail}>{user.email}</p>
                                </div>
                            </div>
                        )}

                        {/* Nav links */}
                        <NavLink to="/about" className={mobileLinkClass}>
                            {t('menuAbout')}
                        </NavLink>

                        {userRole === 2 && (
                            <NavLink to="/admin/roles" className={mobileLinkClass}>
                                {t('adminPanel') || 'Admin paneli'}
                            </NavLink>
                        )}

                        {/* Teacher submenu */}
                        {userRole === 1 && (
                            <>
                                <span className={s.mobileSectionLabel}>{t('teacherPanel') || 'Pedagog paneli'}</span>
                                {teacherLinks.map(({ to, label }) => (
                                    <NavLink key={to} to={to} className={mobileSubLinkClass}>
                                        {label}
                                    </NavLink>
                                ))}
                            </>
                        )}

                        <div className={s.mobileDivider} />

                        {/* Language switcher */}
                        <span className={s.mobileSectionLabel}>Til / Язык</span>
                        <div className={s.mobileLangGroup}>
                            {['ru', 'en', 'uz'].map(l => (
                                <button
                                    key={l}
                                    className={`${s.mobileLangBtn} ${lang === l ? s.mobileLangBtnActive : ''}`}
                                    onClick={() => changeLanguage(l)}
                                >
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        <div className={s.mobileDivider} />

                        {/* Profile / Logout */}
                        {isAuthenticated ? (
                            <>
                                <NavLink to="/profile" className={mobileLinkClass}>
                                    {t('profile')}
                                </NavLink>
                                <button className={s.mobileLink} onClick={handleLogout}>
                                    {t('logout')}
                                </button>
                            </>
                        ) : (
                            <NavLink to="/login" className={mobileLinkClass}>
                                {t('login')}
                            </NavLink>
                        )}
                    </div>
                </div>
            </>
        )}
        </>
    );
};
